import { useDroppable } from '@dnd-kit/core';
import { CourseOption } from '../../types/course.response';
import { getCourseKey } from '../../utils/courseUtils';
import CourseCardForDragging from './CourseCardForDragging';
import Searchbar from './Searchbar';

interface PlanSidebarProps {
  userCourses: CourseOption[];
  onSaveCourse: (course: CourseOption) => void;
  onDeleteCourse: (course: CourseOption) => void;
}

export default function PlanSidebar({
  userCourses,
  onSaveCourse,
  onDeleteCourse,
}: PlanSidebarProps) {
  const savedCourses = userCourses.filter(
    c => c.userState.status === 'SAVED'
  );

  const unassignedCourses = userCourses.filter(
    c => c.userState.status === 'COMPLETED' && !c.userState.semester
  );

  const { setNodeRef: setSavedRef, isOver: isOverSaved } = useDroppable({
    id: 'sidebar-saved',
    data: { type: 'sidebar-saved' },
  });

  return (
    <div className="flex flex-col h-full">
      <div className="sticky top-0 z-10 bg-white w-full py-2 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">My Courses</h2>
        <Searchbar onAddCourse={onSaveCourse} />
      </div>
      
      <div className="flex-1 overflow-y-auto py-4">
        {/* Saved Courses -- also the drop target for "drag back from semester" */}
        <div
          ref={setSavedRef}
          className={`min-h-[80px] rounded-lg p-3 mb-6 transition-colors duration-200 ${
            isOverSaved
              ? 'bg-blue-50 border-2 border-dashed border-blue-400'
              : 'border-2 border-transparent'
          }`}
        >
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Saved ({savedCourses.length})
          </h3>
          {savedCourses.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              Search above to save courses
            </p>
          ) : (
            savedCourses.map((course) => (
              <CourseCardForDragging
                key={getCourseKey(course)}
                courseOption={course}
                source="sidebar-saved"
                onDeleteCourse={onDeleteCourse}
              />
            ))
          )}
        </div>

        {/* Unassigned Completed Courses */}
        {unassignedCourses.length > 0 && (
          <div className="p-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Completed - Unassigned ({unassignedCourses.length})
            </h3>
            {unassignedCourses.map((course) => (
              <CourseCardForDragging
                key={getCourseKey(course)}
                courseOption={course}
                source="sidebar-unassigned"
                onDeleteCourse={onDeleteCourse}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
